/**
 * Phase 7 Gelombang 1 — Executive Scenarios List
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Target, Plus, LineChart, Scale, ArrowLeft } from "lucide-react";

const STATUS_COLOR = {
  DRAFT:     "bg-slate-100 text-slate-800 border-slate-200",
  SUBMITTED: "bg-amber-100 text-amber-900 border-amber-200",
  APPROVED:  "bg-emerald-100 text-emerald-900 border-emerald-200",
  LOCKED:    "bg-emerald-100 text-emerald-900 border-emerald-300",
  REJECTED:  "bg-rose-100 text-rose-900 border-rose-200",
  ARCHIVED:  "bg-slate-100 text-slate-500 border-slate-200",
};

const EDIT_ROLES = new Set(["super_admin", "direktur_utama", "direktur_operasional", "keuangan", "akuntansi"]);

export default function ExecutiveScenarios() {
  const { user } = useAuth();
  const canCreate = EDIT_ROLES.has(user?.role);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);
  const now = new Date();
  const [form, setForm] = useState({
    name: "", scenario_type: "BASE",
    year: now.getFullYear(), month: now.getMonth() + 1,
    horizon_months: 12, description: "",
  });

  const load = () => {
    api.get("/executive/scenarios")
      .then(({ data }) => setRows(data.items || []))
      .catch((e) => {
        if (e?.response?.status === 403) setErr("Tidak berwenang mengakses skenario eksekutif.");
        else setErr(formatApiError(e));
      });
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name || form.name.length < 2) {
      toast.error("Nama skenario minimal 2 karakter.");
      return;
    }
    try {
      const { data } = await api.post("/executive/scenarios", {
        ...form, year: Number(form.year), month: Number(form.month),
        horizon_months: Number(form.horizon_months),
      });
      toast.success(`Skenario '${data.name}' dibuat (DRAFT).`);
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (err) {
    return (
      <div className="p-6 space-y-4" data-testid="exec-scenarios-page">
        <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
          <ArrowLeft size={12} className="mr-1" />Kembali
        </Link>
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 text-sm" data-testid="exec-scenarios-no-access">
          {err}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="exec-scenarios-page">
      <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
        <ArrowLeft size={12} className="mr-1" />Kembali ke Dashboard Eksekutif
      </Link>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Target className="h-6 w-6 text-sky-700" /> Perencanaan & Skenario
          </h1>
          <p className="text-xs md:text-sm text-slate-500">
            Skenario Base / Optimistic / Conservative / Custom. Projeksi deterministik dengan jejak formula.
            <span className="font-semibold"> Non-posting; tidak mengubah GL/transaksi.</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/executive-scenario-comparison">
            <Button variant="outline" size="sm" data-testid="btn-go-comparison">
              <Scale size={14} className="mr-1" />Perbandingan
            </Button>
          </Link>
          {canCreate && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="btn-create-scenario">
                  <Plus size={14} className="mr-1" />Skenario Baru
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Buat Skenario Baru (DRAFT)</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Nama</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                             data-testid="sc-name" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">Jenis</Label>
                      <Select value={form.scenario_type}
                                onValueChange={(v) => setForm({ ...form, scenario_type: v })}>
                        <SelectTrigger data-testid="sc-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["BASE", "OPTIMISTIC", "CONSERVATIVE", "CUSTOM"].map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Tahun</Label>
                      <Input type="number" value={form.year}
                                onChange={(e) => setForm({ ...form, year: e.target.value })}
                                data-testid="sc-year" />
                    </div>
                    <div>
                      <Label className="text-xs">Bulan</Label>
                      <Input type="number" value={form.month} min={1} max={12}
                                onChange={(e) => setForm({ ...form, month: e.target.value })}
                                data-testid="sc-month" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Horizon (bulan)</Label>
                    <Input type="number" value={form.horizon_months} min={1} max={36}
                             onChange={(e) => setForm({ ...form, horizon_months: e.target.value })}
                             data-testid="sc-horizon" />
                  </div>
                  <div>
                    <Label className="text-xs">Deskripsi (opsional)</Label>
                    <Input value={form.description}
                             onChange={(e) => setForm({ ...form, description: e.target.value })}
                             data-testid="sc-description" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={submit} data-testid="sc-submit">Buat (DRAFT)</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Daftar Skenario</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nama</TableHead>
                <TableHead>Jenis</TableHead>
                <TableHead>Periode</TableHead>
                <TableHead>Horizon</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Versi</TableHead>
                <TableHead>Dibuat</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-xs text-slate-400 italic py-6">
                  Belum ada skenario — buat skenario pertama
                </TableCell></TableRow>
              )}
              {rows.map((s) => (
                <TableRow key={s.id} data-testid={`sc-row-${s.id}`}>
                  <TableCell className="text-xs font-semibold">{s.name}</TableCell>
                  <TableCell className="text-xs">{s.scenario_type}</TableCell>
                  <TableCell className="font-mono text-xs">{s.year}-{String(s.month).padStart(2, "0")}</TableCell>
                  <TableCell className="text-xs">{s.horizon_months} bulan</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_COLOR[s.status] || STATUS_COLOR.DRAFT}
                             data-testid={`sc-status-${s.id}`}>
                      {s.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-xs">v{s.version}</TableCell>
                  <TableCell className="text-xs">
                    {new Date(s.created_at).toLocaleDateString("id-ID")}
                    <div className="text-[10px] text-slate-500">{s.created_by_role}</div>
                  </TableCell>
                  <TableCell>
                    <Link to={`/executive-scenarios/${s.id}`}>
                      <Button variant="outline" size="sm" data-testid={`sc-open-${s.id}`}>
                        <LineChart size={11} className="mr-1" />Buka
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
